import { Map, List } from "immutable";
import React from "react";
import { formatMessage } from "lib/globalize-util";
import { AgGridReact } from "ag-grid-react";
import get from "lodash/get";
import { theme } from "styles/theme";
import { isValidNumber, parseNumber } from "lib/number";

import { merge, $ } from "glamor";
import {
    detailColumnDef,
    BULK_IDENTIFIER,
    SIZE_TOTALS_IDENTIFIER,
    SIZE_IDENTIFIER,
    AllocationGridColumns,
} from "components/product-collection/product-collection-views/product-collection-grid-view/detail-list/helpers";
import {
    CustomPinnedRowRenderer,
    CustomPinnedBulkRowRenderer,
} from "components/product-collection/product-collection-views/product-collection-grid-view/detail-list/customPinnedRowRenderer";
import { AllocationNumberCellEditor } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/allocation-number-cell-editor";
import { TextEditor } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/text-editor";
import { DefaultRendererVanilla } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/default-renderer-vanilla";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import "styles/ag-grid/sizingGrid.styl";

import {
    Item as ProductDisplayFieldPaths,
    SizeCurveStatus,
    SeverityMessages,
    SizingErrors,
    UPC_DELIMITER,
    MANUFACTURED_SIZES_DELIMITER,
    NRF_DELIMITER,
    Kinds,
    CAPTURED_BULKS,
    ItemFields,
    Item,
    ONE_SIZE,
    ItemBagType,
    IField,
    PIPE_CHARACTER_REGEX,
} from "constants/product-collection";
import { DATATEST } from "constants/integration";
import { IDoor } from "helpers/door-collection";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { ColumnTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import {
    ISchemaKey,
    parseSelectedSizes,
    getSizesFieldsToUpdateFromSelectedSizes,
    sizeManufacturedGetter,
    getSizeRelatedFields,
    ScopeKeys,
    changeSizePackField,
    getNewUpcValues,
} from "helpers/product-collection";
import {
    IAllocationDimensionValue,
    IAllocationDimension,
    AllocationBagType,
    AllocationBag,
    BagValues,
    parseLastKeyFromCompoundKey,
    getBagValuesByRowId,
    getAllocationBagValue,
} from "helpers/allocation-dimension";
import {
    ColumnGroupOpenedEvent,
    CellValueChangedEvent,
    GridReadyEvent,
    GridApi,
    ColumnApi,
    GetMainMenuItemsParams,
    ColGroupDef,
} from "ag-grid-community";
import { AlertNotification, AlertCloseMode } from "components/alert/alert-notification";

import { ProductCollectionItemSizing } from "./product-collection-item-sizing";
import {
    IGridParamsQueryParam,
    IPreference,
} from "./product-collection-views/product-collection-grid-view/tool-panels";
import { getDelimitedSizeValues, getDelimiterForSizeLevelColumn } from "helpers/sizing";
import { resetSelectedCellRanges, suppressDeleteBackspaceKeyboard } from "helpers/ag-grid";
import {
    doSizesHaveQuantityLegacy,
    doesBulksAndAllocationsMatch,
    doesBulksAndAllocationsMatchLegacy,
    doSizesHaveQuantity,
} from "helpers/allocation";
import { IPackInfo } from "reducers/collection";
import {
    SizeLayout,
    SizePacksCalculatedColumns,
    SizePacksDataColumns,
    SizePacksTable,
} from "components/size-packs/size-packs-table";
import { amphibiousGet, amphibiousGetIn } from "helpers/objUtils";
import { AllocationDimensionClipboardActions, DOOR_UNIT_CHANGED, Location, SizeActions } from "constants/analytics";
import { findOneFieldByKind } from "epics/collections/helpers/allocation";
import { HeaderCompWithNote } from "./product-collection-views/product-collection-grid-view/grid-renderers/notes-header-component";
import { HeaderCompWithMenu } from "./product-collection-views/product-collection-grid-view/grid-renderers/menu-group-header-comp";
import {
    copyAndPasteUnits,
    CopyCutPasteActions,
    copyCutPasteMenuHandler,
    cutAndPasteUnits,
    IAllocationClipboardState,
    UnitsPath,
} from "helpers/product-collection/units-copy-paste";
import { IMutateAllocationBagValue } from "actions/collections";

import { IClipboard } from "./product-collection-views";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { ICurrency } from "./product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { KEYBOARD_KEYS } from "constants/keyboard";

export const HEADER_HEIGHT = 77;
const ROW_HEIGHT = 28;

export const ALLOCATION_GRID_COMPONENTS = {
    customPinnedRowRenderer: CustomPinnedRowRenderer,
    numberCellEditor: AllocationNumberCellEditor,
    textEditor: TextEditor,
};

export const ALLOCATION_GRID_COMPONENTS_VANILLA = {
    defaultRenderer: DefaultRendererVanilla,
    headerCompWithNote: HeaderCompWithNote,
    headerCompWithMenu: HeaderCompWithMenu,
};

const ALLOCATION_BULKS_ONLY_GRID_COMPONENTS = {
    customPinnedRowRenderer: CustomPinnedBulkRowRenderer,
    numberCellEditor: AllocationNumberCellEditor,
    textEditor: TextEditor,
};

const ALLOCATION_BULKS_ONLY_GRID_COMPONENTS_VANILLA = {
    defaultRenderer: DefaultRendererVanilla,
    headerCompWithMenu: HeaderCompWithMenu,
};

export const resetSelectedCellsListener = (gridApi, editable, { event, api }: any) => {
    const typedEvent = event as KeyboardEvent;
    const eventKey = typedEvent.key;
    const isDeletionKey = KEYBOARD_KEYS.BACKSPACE === eventKey || KEYBOARD_KEYS.DELETE === eventKey;
    if (isDeletionKey && event.target?.value === undefined && editable) {
        resetSelectedCellRanges(gridApi || api);
    }
};

interface IProductCollectionItemAllocation {
    itemDisplayProperty?: List<any>;
    item?: Map<string, any>;
    onMutateCollectionItem: (item) => void;
    onClearAllocations: (itemId: string) => void;
    onLookupAssortmentErrors: (assortmentsData?: Array<{ [key: string]: string }>) => void;
    readOnly: boolean;
    currentAllocationDimensionView: IPreference[];
    doors?: IDoor[];
    dates?: IAllocationDimensionValue[];
    doorClassificationSchema?: ISchemaKey[];
    dimensionsPanelApplied: DimensionsPanelReduxState;
    gridParams?: IGridParamsQueryParam;
    onFetchCollectionPOI: (poi: string) => void;
    onColumnGroupOpened: (groupId: string, isExpanded: boolean) => void;
    collectionId?: string;
    rowIndex?: number;
    isRollupMode?: boolean;
    isServerRollup?: boolean;
    sizingAllocationEnabled: boolean;
    allocationDimensions?: IAllocationDimension[];
    itemsRevision: number;
    editable: boolean;
    isMobile: boolean;
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    merchantFeaturePreference?: nuorder.collection.merchant_feature_config.MerchantFeatureConfig;
    poiToItemsToSizeBulksMap?: Map<string, Map<string, number>>;
    matchingSizePacks?: IPackInfo[];
    sizePacks?: { [key: string]: { [key1: string]: IPackInfo } };
    sizePackEnabled?: boolean;
    onMutateBagValues: (value: IMutateAllocationBagValue) => void;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    assortmentDimensionMetrics?: boolean;
    merchantCurrency?: ICurrency;
    rootGridApi: GridApi;
}

const getRowNodeId = (data) => {
    return data.size;
};

const isPinnedRow = (node) => {
    return node.rowIndex === 0 && node.rowPinned === "top";
};

export const rowClassRules = {
    "is-pinned": ({ node }) => {
        return isPinnedRow(node);
    },
};

export const getUpdatedSizesCurveAndStatus = (
    item: any,
    selectedSizes: string[],
    manualSizes: string[],
    allocationDimensions: IAllocationDimension[],
    allocationBag?: AllocationBag
) => {
    const rowId = item.getIn(ProductDisplayFieldPaths.FIELDS.ID);
    const updatedBulks = allocationBag
        ? getBagValuesByRowId(AllocationBagType.bulks, allocationBag, rowId)
        : readBulks(item);
    const updatedAllocations = allocationBag
        ? getBagValuesByRowId(AllocationBagType.allocations, allocationBag, rowId)
        : readAllocations(item);

    const allocationFromSelectedSizes = allocationBag
        ? getAllocationsFromSelectedSizes(updatedAllocations, selectedSizes.concat(manualSizes))
        : getAllocationsFromSelectedSizesLegacy(updatedAllocations, selectedSizes.concat(manualSizes));

    const totalsMatch = allocationBag
        ? doesBulksAndAllocationsMatch(allocationFromSelectedSizes as BagValues, updatedBulks, allocationDimensions)
        : doesBulksAndAllocationsMatchLegacy(allocationFromSelectedSizes, updatedBulks, allocationDimensions);

    const selectedSizesHaveQuantity = allocationBag
        ? doSizesHaveQuantity(selectedSizes, updatedAllocations, allocationDimensions)
        : doSizesHaveQuantityLegacy(selectedSizes, updatedAllocations, allocationDimensions);
    const manualSizesHaveQuantity = allocationBag
        ? doSizesHaveQuantity(manualSizes, updatedAllocations, allocationDimensions)
        : doSizesHaveQuantityLegacy(manualSizes, updatedAllocations, allocationDimensions);

    const isOneSize = selectedSizes.length === 1 && selectedSizes[0] === ONE_SIZE;

    let sizeCurve = item.getIn(ProductDisplayFieldPaths.FIELDS.SIZE_CURVE) || "";
    let sizeCurveStatus: SizingErrors | SizeCurveStatus;

    // When sizes do not have quantities OR when there is no selected sizes
    if (!selectedSizesHaveQuantity && !manualSizesHaveQuantity) {
        sizeCurveStatus = SizeCurveStatus.None;
    } else if (totalsMatch) {
        sizeCurve = !isOneSize ? SizeCurveStatus.Manual : "";
        sizeCurveStatus = SizeCurveStatus.Manual;

        if (manualSizes.length > 0 && !manualSizesHaveQuantity) {
            sizeCurveStatus = SizingErrors.CurveContainsZeroUnits;
        }
    } else {
        sizeCurve = sizeCurve !== SizeCurveStatus.Manual ? sizeCurve : SizeCurveStatus.Manual;
        sizeCurveStatus = SizingErrors.SizeTotalsDotNotMatchBulks;
    }

    return {
        sizeCurve,
        sizeCurveStatus,
    };
};

export const getAllocationsFromSelectedSizesLegacy = (allocationsData, selectedSizes) => {
    const allocations = get(allocationsData, "r", []);

    const allocationsBySelectedSizes = allocations.filter((allocation) => {
        const allocationData = get(allocation, "c", []);

        // Take 2nd from the last as it's the size.
        const size = allocationData[allocationData.length - 2];

        return selectedSizes.includes(size);
    });

    return { r: allocationsBySelectedSizes };
};

export const getAllocationsFromSelectedSizes = (bagValues: BagValues, selectedSizes) => {
    const allocationsBySelectedSizes = bagValues.filter((_bagValue, key) => {
        // Last id in the compound key is the size.
        const size = parseLastKeyFromCompoundKey(key);

        return selectedSizes.includes(size);
    });

    return allocationsBySelectedSizes;
};

export const defaultColDef = {
    resizable: true,
    suppressKeyboardEvent: suppressDeleteBackspaceKeyboard,
};

export const styles = merge(
    {
        width: "100%",
    },
    $(".sizeContainer", {
        borderLeftWidth: "0px",
        borderRightWidth: "0px",
        padding: "0 0 4px",
        marginBottom: "16px",
    }),
    $(" .notification", {
        marginBottom: "12px !important",
    }),
    $(" .sizingGrid", {
        width: "100%",
        margin: "0",
    }),
    $(" .sizingGrid .ag-root-wrapper-body", {
        height: "auto",
    }),
    $(" .ag-status-bar", {
        display: "none",
    }),
    $(" .doorMode", {
        display: "flex",
        alignItems: "center",
    }),
    $(" .doorMode > label", {
        fontSize: "14px",
        fontWeight: "bold",
        color: theme.palette.darkestGrey,
    }),
    $(" .totals", {
        textAlign: "right !important",
    }),
    $(" .ag-theme-balham .ag-row-first.ag-row-selected", {
        backgroundColor: theme.palette.selectedOptionBackground,
    }),
    $(" .ag-theme-balham .doorsGroupTotal", {
        backgroundColor: theme.palette.lighterBlue,
    }),
    $(" .ag-theme-balham .doorsGroupTotal--notEditable,  .ag-theme-balham .sizeColumn--notEditable", {
        backgroundColor: theme.grid.headerBackgroundColor,
    }),
    $(" .ag-theme-balham .notEditable", {
        backgroundColor: theme.grid.headerBackgroundColor,
    }),
    $(" .ag-theme-balham .doorsGroupTotal--pinned", {
        backgroundColor: theme.grid.headerBackgroundColor,
    }),
    $(".ag-theme-balham .allocationDimensionTotal", {
        backgroundColor: theme.grid.totalAllocationDimensionColumn,
    }),
    $(" .ag-theme-balham .doorsGroupClassification", {
        color: theme.palette.blue,
        fontSize: "11px",
        marginLeft: "2px",
        fontStyle: "italic",
    }),
    $(" .ag-theme-balham .bulksCell", {
        fontWeight: "bold !important",
    }),
    $(".sizeContainer div.size-packs-grid", {
        height: "300px",
    })
);

export const useColumnTypes = () =>
    React.useMemo(
        () => ({
            [ColumnTypes.AllocationDimensionTotal]: {},
            [ColumnTypes.ClassificationTotal]: {},
            [ColumnTypes.DoorBulkQuantity]: {},
            [ColumnTypes.AllDimensionTotal]: {},
            [ColumnTypes.NestedDimensionTotal]: {},
            [ColumnTypes.SizesTotal]: {},
        }),
        []
    );

const useColumnTypesToFieldsMapping = () =>
    React.useMemo(
        () => ({
            [AllocationGridColumns.UPC]: ProductDisplayFieldPaths.FIELDS.UPC,
            [AllocationGridColumns.NRFSizeCode]: ProductDisplayFieldPaths.FIELDS.NRF_SIZE_CODE,
            [AllocationGridColumns.NRFSizeDescription]: ProductDisplayFieldPaths.FIELDS.NRF_SIZE_DESCRIPTION,
        }),
        []
    );

export const isDoor = ({ path, dates }) => path.length === 2 || (path.length === 1 && !dates.length);

const useAllocationClipboard = (
    props: Pick<
        IProductCollectionItemAllocation,
        | "dates"
        | "onMutateCollectionItem"
        | "item"
        | "allocationDimensions"
        | "allocationBagRef"
        | "onMutateBagValues"
        | "doors"
    >,
    sizeCurveField: IField,
    sizeCurveStatusField: IField
) => {
    const [allocationClipboardState, setAllocationClipboardState] = React.useState<IAllocationClipboardState>();
    const allocationClipboardStateRef = React.useRef<IAllocationClipboardState>();
    allocationClipboardStateRef.current = allocationClipboardState;

    const [clipboard, setClipboard] = React.useState<IClipboard>();
    const clipboardRef = React.useRef<IClipboard>();
    clipboardRef.current = clipboard;

    const handleCutCopyPasteActions = React.useCallback(
        (path: UnitsPath, action: CopyCutPasteActions) => {
            const { dates, onMutateCollectionItem, item, allocationDimensions, allocationBagRef } = props;

            const srcIsDoor = isDoor({ path, dates });
            const stringSelectedsizes = item ? item.getIn(ProductDisplayFieldPaths.FIELDS.SIZES_SELECTED, "") : "";
            const {
                curveSizes: selectedSizes,
                sizesAddedManually: manualSizes,
                allSizes,
            } = parseSelectedSizes(stringSelectedsizes);
            const { sizeCurve, sizeCurveStatus } = getUpdatedSizesCurveAndStatus(
                item,
                selectedSizes,
                manualSizes,
                allocationDimensions,
                allocationBagRef?.allocationBag
            );

            let newState = { path, action, srcIsDoor };
            if (action === CopyCutPasteActions.Paste) {
                const { path: statePath, action: stateAction } = allocationClipboardState ?? {};
                let newItem = Object.values(ItemBagType).reduce(
                    (acc, curr) =>
                        (stateAction === CopyCutPasteActions.Copy ? copyAndPasteUnits : cutAndPasteUnits)(
                            acc,
                            statePath,
                            path,
                            curr
                        ),
                    item
                );

                if (sizeCurveField) {
                    newItem = newItem.setIn(["fields", sizeCurveField.field_name, "string_value"], sizeCurve);
                }

                if (sizeCurveStatusField) {
                    newItem = newItem.setIn(
                        ["fields", sizeCurveStatusField.field_name, "string_value"],
                        sizeCurveStatus
                    );
                }
                onMutateCollectionItem(newItem);
                newState = undefined;

                const dimensionType = srcIsDoor ? "door" : "delivery";
                const bagType = ItemFields.Allocation;
                (window as any).analytics.track(AllocationDimensionClipboardActions[stateAction], {
                    dimensionType,
                    bagType,
                    sizeCount: allSizes?.length,
                    productCount: 1,
                });
            }
            setAllocationClipboardState(newState);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            allocationClipboardState,
            setAllocationClipboardState,
            props.dates,
            props.onMutateCollectionItem,
            props.item,
            props.allocationDimensions,
            props.allocationBagRef,
        ]
    );

    const handleCutCopyPasteActionsRef = React.useRef<(path: UnitsPath, action: CopyCutPasteActions) => void>();
    handleCutCopyPasteActionsRef.current = handleCutCopyPasteActions;

    const pasteAllocation = ({ groupId: targetGroup, doorId: targetDoor }) => {
        const { groupId: sourceGroup, doorId: sourceDoor, action } = clipboard;
        const { item, allocationDimensions, allocationBagRef, onMutateCollectionItem, doors, onMutateBagValues } =
            props;
        const itemId = item.getIn(ProductDisplayFieldPaths.FIELDS.ID);
        const stringSelectedsizes = item ? item.getIn(ProductDisplayFieldPaths.FIELDS.SIZES_SELECTED, "") : "";
        const {
            curveSizes: selectedSizes,
            sizesAddedManually: manualSizes,
            allSizes,
        } = parseSelectedSizes(stringSelectedsizes);
        const { sizeCurve, sizeCurveStatus } = getUpdatedSizesCurveAndStatus(
            item,
            selectedSizes,
            manualSizes,
            allocationDimensions,
            allocationBagRef.allocationBag
        );

        let newItem = item;
        if (sizeCurveField) {
            newItem = newItem.setIn(["fields", sizeCurveField.field_name, "string_value"], sizeCurve);
        }
        if (sizeCurveStatusField) {
            newItem = newItem.setIn(["fields", sizeCurveStatusField.field_name, "string_value"], sizeCurveStatus);
        }
        onMutateCollectionItem(newItem.delete(ItemFields.Allocation).delete(ItemFields.Bulks));

        const doorOptions = sourceDoor ? [sourceDoor] : doors.map((door) => door.id);
        doorOptions.forEach((id) => {
            const bagType = AllocationBagType.bulks;
            const quantity = getAllocationBagValue(
                bagType,
                allocationBagRef?.allocationBag as unknown as AllocationBag,
                itemId,
                [sourceGroup, id]
            );
            onMutateBagValues({
                bagType,
                rowId: itemId,
                dimensionValues: [targetGroup, sourceDoor ? targetDoor : id],
                quantity,
            });
            if (action === CopyCutPasteActions.Cut) {
                onMutateBagValues({ bagType, rowId: itemId, dimensionValues: [sourceGroup, id], quantity: 0 });
            }

            allSizes.forEach((size) => {
                const bagType = AllocationBagType.allocations;
                const quantity = getAllocationBagValue(
                    bagType,
                    allocationBagRef?.allocationBag as unknown as AllocationBag,
                    itemId,
                    [sourceGroup, id, size]
                );
                onMutateBagValues({
                    bagType,
                    rowId: itemId,
                    dimensionValues: [targetGroup, sourceDoor ? targetDoor : id, size],
                    quantity,
                });
                if (action === CopyCutPasteActions.Cut) {
                    onMutateBagValues({
                        bagType,
                        rowId: itemId,
                        dimensionValues: [sourceGroup, id, size],
                        quantity: 0,
                    });
                }
            });
        });
        setClipboard(undefined);

        // Analytics reporting
        const dimensionType = sourceDoor ? "door" : "delivery";
        const bagType = ItemFields.Allocation;
        (window as any).analytics.track(AllocationDimensionClipboardActions[action], {
            dimensionType,
            bagType,
            sizeCount: allSizes?.length,
            productCount: 1,
        });
    };

    const handleGetMainMenuItems = React.useCallback(
        ({ column, defaultItems }: GetMainMenuItemsParams) => {
            const { allocationBagRef } = props;
            if (!allocationBagRef?.allocationBag) {
                const clipboardState = allocationClipboardStateRef.current;
                return (
                    copyCutPasteMenuHandler(
                        !clipboardState,
                        !!clipboardState?.srcIsDoor,
                        column,
                        handleCutCopyPasteActionsRef.current
                    ) ?? []
                );
            }

            const colDef = column.getColDef();
            const { groupId = (colDef as ColGroupDef).groupId, doorId } = colDef.cellRendererParams || {};

            if (!groupId && !doorId) {
                return defaultItems;
            }

            return [
                {
                    name: formatMessage("Copy Units"),
                    action: () => setClipboard({ groupId, doorId, action: CopyCutPasteActions.Copy }),
                },
                {
                    name: formatMessage("Cut Units"),
                    action: () => setClipboard({ groupId, doorId, action: CopyCutPasteActions.Cut }),
                },
                {
                    name: formatMessage("Paste Units"),
                    action: () => pasteAllocation({ groupId, doorId }),
                    disabled: !clipboard,
                },
            ];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clipboard, props]
    );

    return { getMainMenuItems: handleGetMainMenuItems };
};

const useItemRef = (item, revision) => {
    const itemId = item?.getIn(ProductDisplayFieldPaths.FIELDS.ID);

    const itemRef = React.useRef(item);
    const bulksRef = React.useRef(undefined);
    const allocationsRef = React.useRef(undefined);
    const sizeLevelFieldsRef = React.useRef(undefined);

    // For tracking active revision. We need this to make sure this component shows snapshot of latest revision, else we will have race-condition that wipes previously entered units.
    const localItemsRevision = React.useRef(0);

    // We increment this count in setter because setter is not deferred. We'll decrement this counter in `onCellChange` eventually.
    // Ag Grid's `onCellChange` event is deferred, there is a window when previously sent bags are received and replace current bags that erase units entered inside that window.
    // This is used to prevent current bags from being replaced by incoming (our changes or remote changes) bags until all changes are handled by `onCellChange`.
    const localLockRef = React.useRef(0);

    // Reset when item changes
    React.useEffect(() => {
        localItemsRevision.current = 0;
        localLockRef.current = 0;
        bulksRef.current = undefined;
        allocationsRef.current = undefined;
        sizeLevelFieldsRef.current = undefined;
        itemRef.current = item;
    }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (localLockRef.current === 0 && (localItemsRevision.current < revision || (revision ?? true) === true)) {
            localItemsRevision.current = revision;
            bulksRef.current = readBulks(item);
            allocationsRef.current = readAllocations(item);
            sizeLevelFieldsRef.current = readSizeLevelFields(item);
            itemRef.current = item;
        }
    }, [revision, itemId]); // eslint-disable-line react-hooks/exhaustive-deps

    const incrementLock = () => {
        localLockRef.current++;
    };

    const decrementLock = () => {
        if (localLockRef.current > 0) {
            localLockRef.current--;
        }
    };

    return {
        incrementLock,
        decrementLock,
        itemRef,
        bulksRef,
        allocationsRef,
        sizeLevelFieldsRef,
        localRevision: localItemsRevision.current,
    };
};

export const ProductCollectionItemAllocation: React.FunctionComponent<IProductCollectionItemAllocation> = ({
    itemDisplayProperty,
    item,
    onMutateCollectionItem,
    onClearAllocations,
    onLookupAssortmentErrors,
    readOnly,
    doors,
    dates,
    doorClassificationSchema,
    dimensionsPanelApplied,
    gridParams,
    onColumnGroupOpened,
    collectionId,
    isRollupMode,
    isServerRollup,
    sizingAllocationEnabled,
    allocationDimensions,
    itemsRevision,
    currentAllocationDimensionView,
    editable,
    isMobile,
    merchantPurchaseOrderDoorGroups,
    merchantFeaturePreference,
    poiToItemsToSizeBulksMap,
    onFetchCollectionPOI,
    matchingSizePacks,
    sizePackEnabled,
    sizePacks,
    onMutateBagValues,
    allocationBagRef,
    assortmentDimensionMetrics,
    merchantCurrency,
    rootGridApi,
}) => {
    const { incrementLock, decrementLock, itemRef, bulksRef, allocationsRef, localRevision, sizeLevelFieldsRef } =
        useItemRef(item, itemsRevision);
    const isLegacyBag = !allocationBagRef?.allocationBag;

    const sizePacksApplicable = React.useMemo(() => {
        return sizePackEnabled && !!amphibiousGetIn(itemRef.current, ProductDisplayFieldPaths.FIELDS.PACK_TYPE);
    }, [localRevision, sizePackEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    const hasPPSData =
        item?.getIn(ProductDisplayFieldPaths.FIELDS.COST_VENDOR_PPS) !== undefined &&
        item?.getIn(ProductDisplayFieldPaths.FIELDS.RETAIL_VENDOR_PPS) !== undefined;

    const allocationColumnDef = React.useMemo(
        () =>
            detailColumnDef(
                doors,
                dates,
                doorClassificationSchema,
                dimensionsPanelApplied,
                readOnly,
                gridParams,
                currentAllocationDimensionView,
                isMobile,
                merchantPurchaseOrderDoorGroups,
                allocationBagRef,
                undefined,
                itemDisplayProperty.filter((schemaKey) => schemaKey.get("field_name") === CAPTURED_BULKS).toJS(),
                rootGridApi,
                incrementLock,
                assortmentDimensionMetrics && !isLegacyBag,
                hasPPSData,
                merchantCurrency
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMobile, itemDisplayProperty, hasPPSData]
    );

    const agGridApiRef = React.useRef<GridApi>(null);
    const agGridColumnApiRef = React.useRef<ColumnApi>(null);

    const itemId = item?.getIn(ProductDisplayFieldPaths.FIELDS.ID);

    // This will force one more render pass when `itemId` changes in case of next/previous arrow.
    // The additional render pass is needed by `useMemo` to pick up new changes made to `itemRef`, `allocationRef` and `bulksRef`.
    const [tick, setTick] = React.useState<number>(0);
    React.useEffect(() => {
        setTick((currentTick) => currentTick + 1);
    }, [itemId]);

    const vendorSizes = React.useMemo(() => {
        const sizes = sizeManufacturedGetter(null, { data: item?.toJS() });
        return (sizes || "").split(MANUFACTURED_SIZES_DELIMITER);
    }, [localRevision, itemId]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedSizes = React.useMemo(() => {
        const sizes = item.getIn(ProductDisplayFieldPaths.FIELDS.SIZES_SELECTED);
        return (sizes || "").replace(PIPE_CHARACTER_REGEX, "").split(MANUFACTURED_SIZES_DELIMITER);
    }, [item]);

    const { allSizes: itemSizes, sizesAddedManually } = React.useMemo(() => {
        const stringSelectedsizes = itemRef.current
            ? itemRef.current.getIn(ProductDisplayFieldPaths.FIELDS.SIZES_SELECTED, "")
            : "";
        return parseSelectedSizes(stringSelectedsizes);
    }, [itemRef.current, isRollupMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const { sizeCurveMethodField, sizeCurveStatusField, sizeCurveField } = React.useMemo(() => {
        if (!itemDisplayProperty) {
            return {};
        }

        return getSizeRelatedFields(itemDisplayProperty);
    }, [itemDisplayProperty]);

    const handleCellChange = React.useCallback(
        (event: CellValueChangedEvent) => {
            const { oldValue, newValue, context, data, column, colDef } = event;
            // Clean non-numeric and 0
            const cleanedNewValue = isValidNumber(newValue) ? newValue : undefined;

            decrementLock();

            if (oldValue === cleanedNewValue) {
                return;
            }

            let item = itemRef.current;
            const analytics = (global as any).analytics as any;
            const isSizeLevelField = [
                AllocationGridColumns.UPC,
                AllocationGridColumns.NRFSizeCode,
                AllocationGridColumns.NRFSizeDescription,
            ].includes(colDef.field as AllocationGridColumns);
            if (data.type === BULK_IDENTIFIER) {
                if (isLegacyBag) {
                    // bulksRef.current is mutated in ag grid value setters
                    item = item.setIn(ProductDisplayFieldPaths.BULKS, get(data, ItemFields.Bulks).current);
                }
                analytics?.track(DOOR_UNIT_CHANGED, {
                    location: data.type ? Location.Cdp : Location.Grid,
                    doorId: colDef.cellRendererParams.doorId,
                    groupId: column.getParent()?.getGroupId,
                    headerName: colDef.headerName,
                    colId: column.getColId(),
                });
            } else if (data.type === SIZE_IDENTIFIER) {
                if (isSizeLevelField) {
                    Object.keys(columnTypeToFieldMapping).forEach(
                        (key) =>
                            (item = item.setIn(
                                columnTypeToFieldMapping[key],
                                sizeLevelFieldsRef.current[key].join(getDelimiterForSizeLevelColumn(key))
                            ))
                    );
                } else {
                    if (isLegacyBag) {
                        item = item.setIn(
                            ProductDisplayFieldPaths.ALLOCATIONS,
                            get(data, ItemFields.Allocation).current
                        );
                    }

                    analytics?.track(SizeActions.SizeUnitsChanged, {
                        colId: column.getColId(),
                        doorId: colDef.cellRendererParams.doorId,
                        groupId: column.getParent()?.getGroupId,
                        headerName: colDef.headerName,
                        size: data.size,
                    });
                }
            }

            if (!isSizeLevelField) {
                const stringSelectedsizes = item ? item.getIn(ProductDisplayFieldPaths.FIELDS.SIZES_SELECTED, "") : "";
                const { curveSizes: selectedSizes, sizesAddedManually: manualSizes } =
                    parseSelectedSizes(stringSelectedsizes);
                const { sizeCurve, sizeCurveStatus } = getUpdatedSizesCurveAndStatus(
                    item,
                    selectedSizes,
                    manualSizes,
                    allocationDimensions,
                    context.allocationBagRef?.allocationBag
                );

                if (sizeCurveField) {
                    item = item.setIn(["fields", sizeCurveField.field_name, "string_value"], sizeCurve);
                }

                if (sizeCurveStatusField) {
                    item = item.setIn(["fields", sizeCurveStatusField.field_name, "string_value"], sizeCurveStatus);
                }
            }
            onMutateCollectionItem(item);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [vendorSizes, isLegacyBag]
    ); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSizeFieldUpdated = React.useCallback(
        (fieldPaths, value, { rawItemDisplayProperty, sizeManufactured, sizeRanges }) => {
            const { allSizes } = parseSelectedSizes(value);
            const { sizeCurveMethod, sizeCurve, sizeCurveStatus } = getSizesFieldsToUpdateFromSelectedSizes(
                allSizes as string[],
                sizeRanges,
                rawItemDisplayProperty,
                sizeManufactured
            );

            let item = itemRef.current;
            const oldSizeRange = item.getIn(Item.FIELDS.SIZES_SELECTED);
            const oldUpcRange = item.getIn(Item.FIELDS.UPC);

            item = item.setIn(fieldPaths, value);

            const isSMU = !item.getIn(Item.FIELDS.PRODUCT_ID);
            if (isSMU) {
                if (oldSizeRange !== value) {
                    const newUpcValues = getNewUpcValues(oldSizeRange, value, oldUpcRange);
                    item = item.setIn(Item.FIELDS.UPC, newUpcValues);
                }
            }

            const packResultsField = itemDisplayProperty.find((c) => c.get("kind") === Kinds.PACKS_RESULTS);

            if (packResultsField) {
                item = item.deleteIn(["fields", packResultsField.get("field_name"), "string_value"]);
            }

            if (sizeCurveMethodField) {
                item = item.setIn(["fields", sizeCurveMethodField.field_name, "string_value"], sizeCurveMethod);
            }

            if (sizeCurveField) {
                item = item.setIn(["fields", sizeCurveField.field_name, "string_value"], sizeCurve);
            }

            if (sizeCurveStatusField) {
                item = item.setIn(["fields", sizeCurveStatusField.field_name, "string_value"], sizeCurveStatus);
            }

            onClearAllocations(item.getIn(ProductDisplayFieldPaths.FIELDS.ID));
            onMutateCollectionItem(item);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const rawItemDisplayProperty = React.useMemo(() => itemDisplayProperty.toJS(), [itemDisplayProperty]);
    const handlePacksFieldUpdated = React.useCallback(
        (fieldPaths, value) => {
            let item = itemRef.current;
            const prevValue = amphibiousGet(item, fieldPaths);

            if (value === prevValue) {
                return;
            }

            if ([Item.FIELDS.PACKS_SELECTED, Item.FIELDS.PACK_TYPE].includes(fieldPaths)) {
                const { sizeCurveStatusField } = getSizeRelatedFields(itemDisplayProperty);
                if (sizeCurveStatusField) {
                    item = item.setIn(
                        ["fields", sizeCurveStatusField.field_name, "string_value"],
                        SizeCurveStatus.None
                    );
                }
            }

            const relevantSchemas = {
                packsPerDoorSchema: findOneFieldByKind(Kinds.PACKS_MAX_PER_DOOR, rawItemDisplayProperty),
                packsPerProductSchema: findOneFieldByKind(Kinds.PACKS_MAX_PER_PROD, rawItemDisplayProperty),
            };
            item = changeSizePackField(fieldPaths[1], item, value, sizePacks, relevantSchemas);
            onMutateCollectionItem(item);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [itemDisplayProperty]
    );

    const handleColumnGroupOpened = React.useCallback((e: ColumnGroupOpenedEvent) => {
        const { columnGroup } = e;
        onColumnGroupOpened(columnGroup.getGroupId(), columnGroup.isExpanded());
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const { active: editableSizeLevelFields = false } = merchantFeaturePreference?.editable_size_level_fields ?? {};
    const { rowData, summaryRow } = React.useMemo(() => {
        const rowFields = {
            [ProductDisplayFieldPaths.BULKS[0]]: bulksRef,
            [ProductDisplayFieldPaths.ALLOCATIONS[0]]: allocationsRef,
            [ItemFields.Fields]: itemRef.current.toJS()[ItemFields.Fields],
            SizeLevelFields: sizeLevelFieldsRef,
            rowId: itemId,
        };
        const doorSums = {
            type: SIZE_TOTALS_IDENTIFIER,
            size: formatMessage("Door Totals"),
            ...rowFields,
        };

        const bulkDataRow = {
            type: BULK_IDENTIFIER,
            size: formatMessage("Bulk"),
            vendorSizeIndex: -1,
            sizeLevelFieldsEditable: false,
            ...rowFields,
        };

        const rows = [bulkDataRow];
        const isSMU = !item.getIn(Item.FIELDS.PRODUCT_ID);

        itemSizes.forEach((size) => {
            rows.push({
                type: SIZE_IDENTIFIER,
                size,
                vendorSizeIndex: isSMU ? selectedSizes.indexOf(size) : vendorSizes.indexOf(size),
                sizeLevelFieldsEditable: editableSizeLevelFields,
                // Set these values here for getter & setter reference. We don't want to do expensive Immutable <-> JS conversion.
                ...rowFields,
            });
        });

        return { rowData: rows, summaryRow: [doorSums] };
    }, [localRevision, itemSizes, itemId, vendorSizes, editableSizeLevelFields]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOnAgGridReady = React.useCallback((event: GridReadyEvent) => {
        agGridApiRef.current = event.api;
        agGridColumnApiRef.current = event.columnApi;
        refreshRowData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        refreshRowData();
    }, [rowData, summaryRow]); // eslint-disable-line react-hooks/exhaustive-deps

    // Make sure any loading overlay is cleared
    React.useEffect(() => {
        if (allocationBagRef?.allocationBag) {
            agGridApiRef.current?.hideOverlay();
        }
    }, [allocationBagRef?.allocationBag]);

    const refreshRowData = () => {
        if (agGridApiRef.current) {
            agGridApiRef.current.setRowData(rowData);
            // Force refresh pinned row as it's not refreshing on paste and undo/redo
            agGridApiRef.current.setPinnedTopRowData(summaryRow);
            agGridApiRef.current.expireValueCache();
            process.nextTick(() => {
                agGridApiRef.current.refreshCells();
            });
        }

        if (agGridColumnApiRef.current) {
            // Auto-size pinned columns, particularly useful to display long UPC
            agGridColumnApiRef.current.autoSizeColumns([
                AllocationGridColumns.Size,
                AllocationGridColumns.UPC,
                AllocationGridColumns.Totals,
            ]);
        }
    };

    if (!item) {
        return null;
    }

    const [alertCloseMode, setAlertCloseMode] = React.useState<AlertCloseMode>(AlertCloseMode.None); // eslint-disable-line react-hooks/rules-of-hooks
    const [prevSeverity, setPrevSeverity] = React.useState<SeverityMessages>(SeverityMessages.None); // eslint-disable-line react-hooks/rules-of-hooks
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { alertMessage, severity } = React.useMemo(() => {
        const isLegacyBag = !allocationBagRef?.allocationBag;
        let totalsMatch, manualSizesHaveQuantity;

        if (isLegacyBag) {
            const allocationsData = allocationsRef.current || {};
            const bulksData = bulksRef.current || {};
            const allocationFromSelectedSizes = getAllocationsFromSelectedSizesLegacy(allocationsData, itemSizes);
            totalsMatch = doesBulksAndAllocationsMatchLegacy(
                allocationFromSelectedSizes,
                bulksData,
                allocationDimensions
            );
            manualSizesHaveQuantity = doSizesHaveQuantityLegacy(
                sizesAddedManually as string[],
                allocationsData,
                allocationDimensions
            );
        } else {
            const allocationsBagValues = getBagValuesByRowId(
                AllocationBagType.allocations,
                allocationBagRef?.allocationBag,
                itemId
            );
            const bulksBagValues = getBagValuesByRowId(
                AllocationBagType.bulks,
                allocationBagRef?.allocationBag,
                itemId
            );
            const allocationFromSelectedSizes = getAllocationsFromSelectedSizes(allocationsBagValues, itemSizes);
            totalsMatch = doesBulksAndAllocationsMatch(
                allocationFromSelectedSizes as BagValues,
                bulksBagValues,
                allocationDimensions
            );
            manualSizesHaveQuantity = doSizesHaveQuantity(
                sizesAddedManually as string[],
                allocationsBagValues,
                allocationDimensions
            );
        }

        let message;
        let curSeverity = SeverityMessages.None;

        if (totalsMatch) {
            if (sizesAddedManually.length > 0 && !manualSizesHaveQuantity) {
                message = formatMessage(
                    "This product contains at least one manually added size that contains zero units."
                );
                curSeverity = SeverityMessages.Warning;
            }
        } else if (itemSizes.length > 0) {
            if (
                item.getIn(["fields", sizeCurveStatusField.field_name, "string_value"]) ===
                SizingErrors.BulkPackConstraintsMismatch
            ) {
                message = formatMessage("Bulk units do not align with pack constraints. Please update bulk amount.");
            } else {
                message = formatMessage(
                    "Individual size units do not match the bulk units for one or more doors. Please update the bulk amount, or adjust other sizes for the door."
                );
            }
            curSeverity = SeverityMessages.Error;
        }

        setPrevSeverity(curSeverity);
        if (!message) {
            setAlertCloseMode(AlertCloseMode.Automaticlly);
        }

        return {
            alertMessage: prevSeverity === curSeverity && alertCloseMode === AlertCloseMode.Manually ? "" : message,
            severity: curSeverity,
        };
    }, [summaryRow, alertCloseMode, prevSeverity, allocationBagRef]); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
        if (!isRollupMode) {
            onLookupAssortmentErrors();
        }
    }, [summaryRow, alertCloseMode, prevSeverity, isRollupMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const handleCloseAlertNotification = React.useCallback(() => setAlertCloseMode(AlertCloseMode.Manually), []);

    const rows = rowData.length + 1; // pinned row
    const containerHeight = HEADER_HEIGHT + rows * ROW_HEIGHT + 2; /* borders */

    const getContextMenuItems = () => ["copy", "separator", "export"];

    const handleCellKeyDown = resetSelectedCellsListener.bind(null, agGridApiRef.current, editable);

    const processCellFromClipboard = ({ value }) => {
        const input = parseNumber(value);
        return isValidNumber(input) ? input : undefined;
    };

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const columnTypes = useColumnTypes();
    const columnTypeToFieldMapping = useColumnTypesToFieldsMapping(); // eslint-disable-line react-hooks/rules-of-hooks

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const gridContext = React.useMemo(
        () => ({
            poiToItemsToSizeBulksMap,
            onFetchCollectionPOI,
            sizePacksApplicable,
            [ScopeKeys.ReferenceItemInfo]: { id: itemId },
            onMutateBagValues,
            allocationBagRef,
            isServerRollup,
            isDetailView: true,
        }),
        [
            poiToItemsToSizeBulksMap,
            itemId,
            onFetchCollectionPOI,
            sizePacksApplicable,
            onMutateBagValues,
            allocationBagRef,
            isServerRollup,
        ]
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
        // Overcome AgGrid bug not recognizing context when it is being changed
        // simultaneously with other props
        agGridApiRef.current?.refreshCells();
    }, [gridContext]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const SIZEPACKS_TOTAL_COLDEF_OVERRIDE = React.useMemo(() => ({ headerName: formatMessage("Total") }), []);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { getMainMenuItems } = useAllocationClipboard(
        { dates, onMutateCollectionItem, item, allocationDimensions, allocationBagRef, onMutateBagValues, doors },
        sizeCurveField,
        sizeCurveStatusField
    );

    return (
        sizingAllocationEnabled && (
            <div {...styles} className={"sizeContainer"}>
                <AlertNotification
                    message={alertMessage}
                    open={!!alertMessage}
                    noMargin={true}
                    onClose={handleCloseAlertNotification}
                    className="notification"
                    severity={severity}
                />
                <ProductCollectionItemSizing
                    item={itemRef.current}
                    collectionId={collectionId}
                    onSizeFieldUpdated={handleSizeFieldUpdated}
                    onPacksFieldUpdated={handlePacksFieldUpdated}
                    readOnly={readOnly}
                    sizePackEnabled={sizePackEnabled}
                />
                <div
                    data-test={DATATEST.ASSORTMENTS.ALLOCATION_GRID}
                    className={`${theme.agGrid.defaultTheme} sizingGrid`}
                    style={{ minHeight: `${containerHeight}px` }}
                >
                    {
                        <AgGridReact
                            onGridReady={handleOnAgGridReady}
                            columnTypes={columnTypes}
                            debug={false}
                            columnDefs={allocationColumnDef}
                            defaultColDef={defaultColDef}
                            components={
                                sizingAllocationEnabled
                                    ? ALLOCATION_GRID_COMPONENTS_VANILLA
                                    : ALLOCATION_BULKS_ONLY_GRID_COMPONENTS_VANILLA
                            }
                            frameworkComponents={
                                sizingAllocationEnabled
                                    ? ALLOCATION_GRID_COMPONENTS
                                    : ALLOCATION_BULKS_ONLY_GRID_COMPONENTS
                            }
                            enableRangeSelection={true}
                            rowSelection="multiple"
                            enableCellChangeFlash={true}
                            animateRows={true}
                            suppressCopyRowsToClipboard={true}
                            // processCellForClipboard={processCellForClipboard}
                            processCellFromClipboard={processCellFromClipboard}
                            stopEditingWhenCellsLoseFocus={true}
                            pinnedTopRowData={summaryRow}
                            onColumnGroupOpened={handleColumnGroupOpened}
                            onCellValueChanged={handleCellChange}
                            getRowNodeId={getRowNodeId}
                            immutableData={true}
                            rowClassRules={rowClassRules}
                            getContextMenuItems={getContextMenuItems}
                            onCellKeyDown={handleCellKeyDown}
                            enterMovesDownAfterEdit
                            context={gridContext}
                            getMainMenuItems={getMainMenuItems}
                            suppressMenuHide={true}
                        />
                    }
                </div>
                {sizePackEnabled && (
                    <div className="size-packs-grid">
                        <SizePacksTable
                            sizes={matchingSizePacks}
                            autoColumns={[
                                SizePacksDataColumns.PackName,
                                SizePacksDataColumns.PackType,
                                SizePacksDataColumns.PackUPC,
                                SizePacksCalculatedColumns.UnitsPerPack,
                            ]}
                            colDefOverrides={{
                                [SizePacksCalculatedColumns.UnitsPerPack]: SIZEPACKS_TOTAL_COLDEF_OVERRIDE,
                            }}
                            sizeLayout={SizeLayout.AsColumns}
                        />
                    </div>
                )}
            </div>
        )
    );
};

export function readAllocations(item) {
    const allocations = item.getIn(ProductDisplayFieldPaths.ALLOCATIONS);
    return allocations && allocations.toJS ? allocations.toJS() : allocations;
}

export function readBulks(item) {
    const bulks = item.getIn(ProductDisplayFieldPaths.BULKS);
    return bulks && bulks.toJS ? bulks.toJS() : bulks;
}

export function readSizeLevelFields(item) {
    return {
        [AllocationGridColumns.UPC]: getDelimitedSizeValues(item, ProductDisplayFieldPaths.FIELDS.UPC, UPC_DELIMITER),
        [AllocationGridColumns.NRFSizeCode]: getDelimitedSizeValues(
            item,
            ProductDisplayFieldPaths.FIELDS.NRF_SIZE_CODE,
            NRF_DELIMITER
        ),
        [AllocationGridColumns.NRFSizeDescription]: getDelimitedSizeValues(
            item,
            ProductDisplayFieldPaths.FIELDS.NRF_SIZE_DESCRIPTION,
            NRF_DELIMITER
        ),
    };
}
